import { designTokens } from "./designTokens";
import type { ThemeName } from "./designTokens";

export type ThemePreference = "system" | "light" | "dark";
export type ResolvedTheme = ThemeName;

export const THEME_COOKIE = "theme";
export const THEME_STORAGE_KEY = "streaminal-theme";
export const DARK_QUERY = "(prefers-color-scheme: dark)";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;
const SHARED_DOMAIN = "streaminal.com";
const COOKIE_PATTERN = /(?:^|;\s*)theme=([^;]*)/;

export function isThemePreference(value: unknown): value is ThemePreference {
  return value === "system" || value === "light" || value === "dark";
}

/** Cookie first (shared across streaminal.com sites), then localStorage, then "system". */
export function readPreference(): ThemePreference {
  const match = COOKIE_PATTERN.exec(document.cookie);
  if (match && isThemePreference(match[1])) return match[1];
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (isThemePreference(stored)) return stored;
  } catch {
    // Storage can throw (Safari private mode, blocked site data). Fall through.
  }
  return "system";
}

export function systemPrefersDark(): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia(DARK_QUERY).matches;
}

export function resolveTheme(pref: ThemePreference, prefersDark: boolean): ResolvedTheme {
  if (pref === "system") return prefersDark ? "dark" : "light";
  return pref;
}

/** ".streaminal.com" for streaminal.com and its subdomains, otherwise null (localhost, staging IPs). */
export function cookieDomainFor(hostname: string): string | null {
  return hostname === SHARED_DOMAIN || hostname.endsWith(`.${SHARED_DOMAIN}`) ? `.${SHARED_DOMAIN}` : null;
}

export function buildThemeCookie(pref: ThemePreference, hostname: string, secure: boolean): string {
  const parts = [`${THEME_COOKIE}=${pref}`, "Path=/", `Max-Age=${COOKIE_MAX_AGE}`, "SameSite=Lax"];
  const domain = cookieDomainFor(hostname);
  if (domain) parts.push(`Domain=${domain}`);
  if (secure) parts.push("Secure");
  return parts.join("; ");
}

export function writePreference(pref: ThemePreference): void {
  document.cookie = buildThemeCookie(pref, window.location.hostname, window.location.protocol === "https:");
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, pref);
  } catch {
    // The cookie alone is enough.
  }
}

export function applyTheme(resolved: ResolvedTheme): void {
  const root = document.documentElement;
  root.setAttribute("data-theme", resolved);
  root.style.colorScheme = resolved;
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", designTokens.modes[resolved].bg);
}
